// "Who took this call?" (CU-869fnzh44): the person on a Call Logs row. Pure (no React / MUI)
// so it is testable without the components barrel.

// The hover text for how we know who it was.
export const SOURCE_HINTS = {
  click: 'Rang from the CRM',
  desk: 'Signed in at this phone',
  handset: 'This phone belongs to them',
  pill: 'Said they were on this call',
  manual: 'Chosen by a manager',
  transcript: 'Worked out from the call recording — the receptionist said their name',
};

const str = (v) => (typeof v === 'string' ? v.trim() : '');

// Rows reach the screen either raw (snake_case, straight from the API, e.g. the save
// response) or normalised (camelCase); read both.
export function teamMemberOf(row) {
  const id = row?.user ?? row?.userId ?? null;
  const personId = typeof id === 'object' && id !== null ? id.id ?? null : id;
  const name = str(row?.user_name ?? row?.userName);
  const source = str(row?.user_source ?? row?.userSource);
  const handset = str(row?.user_desk_name ?? row?.userDeskName);
  // user_name is the handset name when there is no person, so a person exists only
  // when the row has a user id or a source.
  const hasPerson = personId !== null || Boolean(source);
  return { personId, hasPerson, name, source: hasPerson ? source : '', handset };
}

// A practice user's display name, the way the rest of the app builds it.
export const staffName = (member) => [member?.user?.first_name, member?.user?.last_name].map(str).filter(Boolean).join(' ');

// The people the "Who took this call?" list offers: enabled staff (the app's list holds
// only enabled staff), no superusers, by name. The row's current person stays offered
// even when they are no longer in that list, so the dialog never hides who is chosen.
export function staffOptions(results, current = null) {
  const list = (Array.isArray(results) ? results : [])
    .filter((m) => m && m.id != null && m.user && !m.user.is_superuser && staffName(m))
    .map((m) => ({ id: m.id, name: staffName(m), disabled: false }));
  if (current?.personId != null && current.name && !list.some((o) => String(o.id) === String(current.personId))) {
    list.push({ id: current.personId, name: current.name, disabled: true });
  }
  return list.sort((a, b) => a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }));
}

// The row-menu item, and who may see it: desk-phone (Elevate) calls only, never WhatsApp. The
// page decides Call Logs manage; the server still checks it (403).
export const WHO_TOOK_CALL_ITEM = { key: 'who_took_call', label: 'Who took this call?' };
export const canChangeTeamMember = (row) => Boolean(row?.id) && row.provider === 'elevate' && row.channel !== 'whatsapp';

// The pill's "I'm on this call": a live desk-phone call (the pill only follows desk phones; a call
// that says otherwise is left out) that is answered or on hold, or answered at another desk
// (that pill stays for the claim window so the person really on it can name themselves).
export const canClaimOnPill = (call) => Boolean(call?.callId)
  && (call.state === 'on_call' || call.state === 'held' || call.state === 'answered_elsewhere')
  && (!call.provider || call.provider === 'elevate')
  && call.channel !== 'whatsapp'
  // A DentalSEM staff observer copy has no seat at the practice: nobody to name.
  && call.recipientReason !== 'superuser';
export const pillRowId = (call) => `voice:${call.callId}`;

// A saved row's person on top of a loaded row: only the person's fields, so nothing else on
// the loaded row (outcome, follow-up state) is replaced by an older or newer copy.
export const mergeTeamMember = (row, saved) => ({
  ...row, user: saved.user ?? null, userName: saved.userName ?? null, userSource: saved.userSource || '', userDeskName: saved.userDeskName || '',
});
